import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { cancelMyGift, reactivateMyGift, isOk, messageOf } from "../../../services/gift";
import { dayFromToday } from "./ValidTill";

const formatWhen = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";

// "Cancel this gift" (unused or paused gifts), and the note shown once the
// giver has cancelled or closed it. onCancelled receives the updated gift.
export default function GiftCancelPanel({ gift, onCancelled }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newDay, setNewDay] = useState(dayFromToday(365));

  useEffect(() => {
    if (!confirming) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) setConfirming(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirming, busy]);

  const reactivate = async () => {
    setBusy(true);
    const res = await reactivateMyGift(gift.gift_id, datePassed ? newDay : null);
    setBusy(false);
    if (isOk(res)) {
      toast.success(messageOf(res, "Your gift is active again."));
      onCancelled(res.data.data);
    } else {
      toast.error(messageOf(res, "We couldn't reactivate this gift."));
    }
  };

  // The old Valid Till date has passed: a new one is needed to reactivate.
  const datePassed = Boolean(gift.expires_on) && gift.expires_on < dayFromToday(0);

  if (gift.status === "withdrawn") {
    return (
      <div className="vsr-cancelled-note no-print">
        <p>
          This gift was cancelled{gift.cancelled_at ? ` on ${formatWhen(gift.cancelled_at)}` : ""}. Its Gift Code and redeem link don't
          work while it is cancelled. Changed your mind? You can reactivate it.
        </p>
        {datePassed && (
          <p className="vs-owner-row">
            <label htmlFor="vs-reactivate-day">Its Valid Till date has passed. New Valid Till date:</label>
            <input
              id="vs-reactivate-day"
              type="date"
              className="vsr-input"
              value={newDay}
              min={dayFromToday(0)}
              max={dayFromToday(gift.max_expiry_days || 1095)}
              onChange={(e) => setNewDay(e.target.value)}
            />
          </p>
        )}
        <button type="button" className="vsr-btn vsr-btn-sm" disabled={busy || (datePassed && !newDay)} onClick={reactivate}>
          {busy ? "Reactivating…" : "Reactivate this gift"}
        </button>
      </div>
    );
  }

  if (!gift.can_cancel) return null;

  const cancel = async () => {
    setBusy(true);
    const res = await cancelMyGift(gift.gift_id);
    setBusy(false);
    if (isOk(res)) {
      toast.success(messageOf(res, "Your gift has been cancelled."));
      setConfirming(false);
      onCancelled(res.data.data);
    } else {
      toast.error(messageOf(res, "We couldn't cancel this gift. Please try again."));
      if (res && res.data && res.data.data && res.data.data.gift_id) onCancelled(res.data.data);
      setConfirming(false);
    }
  };

  return (
    <div className="vsr-cancel no-print">
      <button type="button" className="vsr-link vsr-cancel-link" onClick={() => setConfirming(true)}>
        Cancel this gift
      </button>

      {confirming && (
        <div className="vsr-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && setConfirming(false)}>
          <div className="vsr-modal vsr-modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="vsr-cancel-title">
            <div className="vsr-modal-body">
              <h2 id="vsr-cancel-title" className="vsr-cancel-title">
                Cancel this gift?
              </h2>
              <p className="vsr-lead vsr-lead-left">
                The Gift Code and redeem link will stop working for good. The gift creation fee isn't refunded. If you change your mind
                later, VerySoul support can help.
              </p>
              <div className="vsr-modal-actions">
                <button type="button" className="vsr-btn vsr-btn-outline" disabled={busy} onClick={() => setConfirming(false)}>
                  Keep my gift
                </button>
                <button type="button" className="vsr-btn vsr-btn-danger" disabled={busy} onClick={cancel}>
                  {busy ? "Cancelling…" : "Yes, cancel gift"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
